import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import { useCreateSoapNote, useUpdateSoapNote } from 'hooks/useSoap';
import React, { useEffect, useState } from 'react';
import { MdAnalytics, MdNotes } from 'react-icons/md';
import { ClinicalEpisode, SoapNote } from 'types/models';

interface FormState {
  comentario: string;
  rom: string;
  impresion: string;
  indicaciones: string;
}

const EMPTY: FormState = {
  comentario: '',
  rom: '',
  impresion: '',
  indicaciones: '',
};

function fromNote(note: SoapNote): FormState {
  const s = note.subjetivo as Record<string, unknown>;
  const o = note.objetivo  as Record<string, unknown>;
  const a = note.analisis  as Record<string, unknown>;
  const p = note.plan      as Record<string, unknown>;
  return {
    comentario:   String(s?.comentario  ?? s?.motivoSesion  ?? ''),
    rom:          String(o?.rom         ?? o?.rangoMovimiento ?? ''),
    impresion:    String(a?.impresion   ?? a?.diagnosticoFisioterapeutico ?? ''),
    indicaciones: String(p?.indicaciones ?? p?.tecnicasAplicadas ?? ''),
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  episode: ClinicalEpisode;
  editNote?: SoapNote;
}

export default function SoapNoteFormModal({ isOpen, onClose, episode, editNote }: Props) {
  const toast       = useToast();
  const createSoap  = useCreateSoapNote();
  const updateSoap  = useUpdateSoapNote();
  const isEdit      = !!editNote;

  const bgColor         = useColorModeValue('white', 'navy.800');
  const textColor       = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor      = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const inputBg         = useColorModeValue('gray.50', 'navy.700');
  const inputBorder     = useColorModeValue('gray.200', 'whiteAlpha.200');
  const accordionBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  const headerBg        = useColorModeValue('gray.50', 'navy.700');

  const [form, setForm]             = useState<FormState>(EMPTY);
  const [errors, setErrors]         = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(isEdit && editNote ? fromNote(editNote) : EMPTY);
      setErrors({});
    }
  }, [isOpen, editNote, isEdit]);

  const set = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (form.comentario.trim().length < 5) e.comentario = 'Mínimo 5 caracteres';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    subjective: { comentario: form.comentario.trim() },
    objective:  { rom: form.rom.trim() },
    assessment: { impresion: form.impresion.trim() },
    plan:       { indicaciones: form.indicaciones.trim() },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (isEdit && editNote) {
        await updateSoap.mutateAsync({
          patientId: episode.pacienteId,
          episodeId: episode.id,
          soapId:    editNote.id,
          payload,
        });
        toast({ title: `Sesión #${editNote.numeroSesion} actualizada`, status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      } else {
        await createSoap.mutateAsync({
          patientId: episode.pacienteId,
          episodeId: episode.id,
          payload,
        });
        toast({ title: 'Nota SOAP registrada', status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      }
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg =
        status === 422 ? 'El episodio está cerrado o archivado' :
        status === 403 ? 'Solo el autor o admin puede editar esta nota' :
        status === 400 ? (err?.response?.data?.message ?? 'Datos inválidos') :
        (err?.response?.data?.message ?? 'Error al guardar');
      toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const SectionLabel = ({ letter, title, color }: { letter: string; title: string; color: string }) => (
    <Flex align='center' gap='8px'>
      <Flex w='24px' h='24px' borderRadius='6px' bg={color} align='center' justify='center' flexShrink={0}>
        <Text fontSize='xs' fontWeight='800' color='white'>{letter}</Text>
      </Flex>
      <Text fontWeight='700' color={textColor} fontSize='sm'>{title}</Text>
    </Flex>
  );

  const Section = ({
    letter, title, color, fkey, label, placeholder, required = false,
  }: {
    letter: string; title: string; color: string;
    fkey: keyof FormState; label: string; placeholder: string; required?: boolean;
  }) => (
    <AccordionItem border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
      <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
        <Box flex={1} textAlign='left'>
          <SectionLabel letter={letter} title={title} color={color} />
        </Box>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel px='14px' pt='14px' pb='4px'>
        <FormControl isInvalid={!!errors[fkey]} mb='14px'>
          <FormLabel ms='2px' fontSize='xs' fontWeight='700' color={mutedColor} mb='4px'>
            {label}{required && <Text as='span' color='red.400' ms='2px'>*</Text>}
          </FormLabel>
          <Textarea
            value={form[fkey]}
            onChange={set(fkey)}
            placeholder={placeholder}
            rows={4}
            bg={inputBg}
            border='1px solid'
            borderColor={errors[fkey] ? 'red.400' : inputBorder}
            borderRadius='12px'
            fontSize='sm'
            resize='vertical'
            _focus={{ borderColor: errors[fkey] ? 'red.400' : 'brand.500', boxShadow: 'none' }}
          />
          {errors[fkey] && <FormErrorMessage ms='4px'>{errors[fkey]}</FormErrorMessage>}
        </FormControl>
      </AccordionPanel>
    </AccordionItem>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl' scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(4px)' bg='blackAlpha.400' />
      <ModalContent bg={bgColor} borderRadius='20px' mx='4'>
        <ModalHeader pb='0'>
          <Flex align='center' gap='3'>
            <Flex w='40px' h='40px' bg='brand.500' borderRadius='12px' align='center' justify='center' flexShrink={0}>
              <Icon as={MdNotes} color='white' w='20px' h='20px' />
            </Flex>
            <Flex direction='column'>
              <Flex align='center' gap='2'>
                <Text color={textColor} fontSize='lg' fontWeight='800'>
                  {isEdit ? `Editar Sesión #${editNote?.numeroSesion}` : 'Nueva Nota SOAP'}
                </Text>
                {isEdit && editNote && (
                  <Badge colorScheme='brand' borderRadius='full' px='10px' py='4px' fontSize='xs'>
                    Sesión #{editNote.numeroSesion}
                  </Badge>
                )}
              </Flex>
              <Text color='secondaryGray.600' fontSize='sm' fontWeight='400'>
                {episode.codigoHc} — {episode.motivoConsulta.slice(0, 50)}{episode.motivoConsulta.length > 50 ? '…' : ''}
              </Text>
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top='20px' />

        <ModalBody pt='20px' pb='4'>
          <form id='soap-form' onSubmit={handleSubmit}>
            <Accordion allowMultiple defaultIndex={[0, 1, 2, 3]} borderColor={accordionBorder}>
              <Section
                letter='S' title='Subjetivo' color='blue.400'
                fkey='comentario' label='Comentario del paciente' required
                placeholder='Paciente refiere mejoría del 30% respecto a sesión anterior...'
              />
              <Section
                letter='O' title='Objetivo' color='teal.400'
                fkey='rom' label='Hallazgos / Rango de movimiento'
                placeholder='Flexión lumbar limitada a 45°. Lasègue (+) a 50° derecho...'
              />
              <Section
                letter='A' title='Análisis' color='purple.400'
                fkey='impresion' label='Impresión clínica'
                placeholder='Evolución favorable de discopatía L4-L5 en fase subaguda...'
              />
              <Section
                letter='P' title='Plan' color='green.400'
                fkey='indicaciones' label='Indicaciones'
                placeholder='Continuar termoterapia y ejercicios de Williams x10 reps...'
              />
            </Accordion>
          </form>
        </ModalBody>

        <ModalFooter gap='3'>
          <Button variant='light' onClick={onClose}>Cancelar</Button>
          <Button
            type='submit'
            form='soap-form'
            isLoading={isSubmitting}
            leftIcon={<Icon as={isEdit ? MdAnalytics : MdNotes} />}>
            {isEdit ? 'Guardar cambios' : 'Registrar sesión'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
